import ErrorIcon from '@mui/icons-material/Error';
import { Chip, type SxProps, type Theme, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { Icon, type IconType } from '../Icon/Icon';

/**
 * Track whether a chip's label is truncated (text-overflow ellipsis) so the
 * caller can surface the full name in a tooltip only when it doesn't fit.
 * Re-measures on resize; no-ops where ResizeObserver is unavailable (e.g. some
 * test environments), in which case the one-shot measure still runs.
 */
const useLabelOverflow = (text: string) => {
  const chipRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const label = chipRef.current?.querySelector<HTMLElement>('.MuiChip-label');
    if (!label) return;
    const measure = () => setIsOverflowing(label.scrollWidth > label.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(label);
    return () => observer.disconnect();
  }, [text]);

  return { chipRef, isOverflowing };
};

const chipBaseSx: SxProps<Theme> = {
  height: 'auto',
  padding: '3px 0',
  backgroundColor: 'grey.100',
  borderColor: 'grey.300',
  '& .MuiChip-icon': { marginRight: '4px' },
  '& .MuiChip-label': { paddingLeft: 0 },
};

const clickableSx: SxProps<Theme> = {
  ...chipBaseSx,
  cursor: 'pointer',
  '&&:hover': { backgroundColor: 'grey.300' },
};

const unavailableSx: SxProps<Theme> = {
  ...chipBaseSx,
  cursor: 'default',
  border: '1px solid',
  borderColor: 'error.main',
  // TODO: no VDS token for this value — needs designer input (rgba(211,47,47,0.08) error tint)
  backgroundColor: 'rgba(211, 47, 47, 0.08)',
  color: 'error.main',
  '& .MuiChip-icon': { marginRight: '4px', color: 'error.main' },
};

const loadingSx: SxProps<Theme> = {
  ...chipBaseSx,
  cursor: 'default',
  border: '1px solid',
  borderColor: 'grey.400',
  '@keyframes cgChipPulse': {
    '0%, 100%': { borderColor: 'grey.300' },
    '50%': { borderColor: 'grey.500' },
  },
  animation: 'cgChipPulse 1.5s ease-in-out infinite',
};

export interface ConceptGroupChipProps {
  /** Display name of the concept group. */
  name: string;
  /** Owner of the concept group — determines icon (Verantos logo vs person). */
  owner?: string;
  /** Whether the chip can be clicked to open details. @default true */
  clickable?: boolean;
  /** Tooltip shown when the chip is not clickable. */
  unavailableTooltip?: string;
  /** Called with the chip name when clicked. */
  onClick?: (name: string) => void;
  /** Called with the chip name when the delete icon is clicked. Shows delete icon when provided. */
  onDelete?: (name: string) => void;
  /** When true, renders in a "pending" state with pulse animation while concept groups are loading. */
  loading?: boolean;
  /**
   * Caps the chip width; the label then ellipsis-truncates (MUI default) and the
   * full name shows on hover via the overflow tooltip. Use where a chip sits in a
   * width-constrained cell (e.g. the AV Definition "Concept mappings" table) so a
   * long group name doesn't blow out the layout.
   */
  maxWidth?: number | string;
}

const getConceptGroupIcon = (owner: string | undefined): IconType =>
  owner?.toLowerCase() === 'verantos' ? 'verantosLogo' : 'owner';

export const ConceptGroupChip = ({
  name,
  owner,
  clickable = true,
  unavailableTooltip,
  onClick,
  onDelete,
  loading = false,
  maxWidth,
}: ConceptGroupChipProps) => {
  const iconType = getConceptGroupIcon(owner);
  const icon = <Icon id={`cg-chip-${name}`} icon={iconType} height={24} width={24} />;
  const { chipRef, isOverflowing } = useLabelOverflow(name);
  // When a maxWidth is set, cap the chip and let the label ellipsis (MUI default
  // on .MuiChip-label) so a long name truncates instead of overflowing its cell.
  const widthSx: SxProps<Theme> = maxWidth != null ? { maxWidth, minWidth: 0 } : {};

  if (loading) {
    return (
      <Chip
        icon={icon}
        label={name}
        size="small"
        variant="outlined"
        sx={{ ...loadingSx, ...widthSx }}
      />
    );
  }

  if (!clickable) {
    const errorIcon = (
      <ErrorIcon data-testid="ErrorIcon" sx={{ width: 24, height: 24, color: 'error.main' }} />
    );
    const chip = (
      <Chip
        icon={errorIcon}
        label={name}
        size="small"
        variant="outlined"
        onDelete={onDelete ? () => onDelete(name) : undefined}
        sx={{ ...unavailableSx, ...widthSx }}
      />
    );

    return unavailableTooltip ? (
      <Tooltip title={unavailableTooltip} arrow>
        {chip}
      </Tooltip>
    ) : (
      chip
    );
  }

  return (
    <Tooltip title={isOverflowing ? name : ''} arrow>
      <Chip
        ref={chipRef}
        icon={icon}
        label={name}
        size="small"
        variant="outlined"
        onClick={() => onClick?.(name)}
        onDelete={onDelete ? () => onDelete(name) : undefined}
        sx={{ ...clickableSx, ...widthSx }}
      />
    </Tooltip>
  );
};
